<template>
    <AdaptiveDrawer
        v-model:open="drawerOpen"
        always-drawer
        :title="drawerChrome.ariaTitle"
        :dismissible="true"
        :ui="{ drawer: ui.drawer }"
        @close="onClose"
    >
        <template #title>
            <div :class="ui.shellRoot">
                <UButton
                    icon="lucide:x"
                    variant="ghost"
                    color="neutral"
                    :ui="closeButtonUi"
                    :class="ui.closeButton"
                    :aria-label="addressSuggestionCopy.closeAriaLabel"
                    @click="dismiss"
                >
                    <template #leading>
                        <SharedAppIcon
                            name="lucide:x"
                            :size="20"
                            :stroke-width="2.5"
                            :class="ui.closeIcon"
                        />
                    </template>
                </UButton>
                <div :class="ui.shellHead">
                    <SharedEyebrow :ui="{ base: ui.eyebrow }">{{
                        addressSuggestionCopy.eyebrow
                    }}</SharedEyebrow>
                    <h2 :class="ui.title">
                        {{ drawerChrome.titleLead }}<em>{{ drawerChrome.titleEmphasis }}</em>
                    </h2>
                    <p :class="ui.subtitle">{{ drawerChrome.subtitle }}</p>
                </div>
            </div>
        </template>
        <template #body="{ close }">
            <div :class="ui.body">
                <div :class="ui.goldRule" aria-hidden="true" />
                <ul :class="ui.list">
                    <li v-if="drawerChrome.showAlert">
                        <SharedCardBlock
                            :config="verifyAlertConfig"
                            :ui="AUTH_ALERT_CARD_BODY_UI"
                        />
                    </li>
                    <li v-else-if="drawerChrome.showSuggestedRow">
                        <CraneAddressCard
                            selectable
                            verification-pill="suggested"
                            suggested-highlight
                            :address="suggestedCard"
                            @select="close(suggested)"
                        />
                    </li>
                    <li v-if="drawerChrome.showManualRow">
                        <CraneAddressCard
                            :selectable="drawerChrome.manualSelectable"
                            :display-only="!drawerChrome.manualSelectable"
                            :verification-pill="drawerChrome.manualPill"
                            :muted="drawerChrome.manualMuted"
                            :address="originalCard"
                            @select="close(original)"
                        />
                    </li>
                </ul>
                <p v-if="drawerChrome.footer" :class="ui.footer">{{ drawerChrome.footer }}</p>
                <CraneButton
                    brand="dashed"
                    width="fluid"
                    :class="ui.editButton"
                    :ui="{ wrapper: 'w-full' }"
                    @click="onEditAddress"
                >
                    <span class="inline-flex items-center gap-2.5">
                        <SharedAppIcon name="lucide:pen" :size="16" class="shrink-0" />
                        {{ addressSuggestionCopy.editAddressLabel }}
                    </span>
                </CraneButton>
            </div>
        </template>
    </AdaptiveDrawer>
</template>

<script setup lang="ts">
/**
 * Layout-hosted AdaptiveDrawer (same shell as AuthDrawer). Opened via
 * `useAddressSuggestionOverlay` — not Nuxt `useOverlay`, which stacked a second
 * scrim over the sheet. `alwaysDrawer` + elevated z-index so this sheet stacks
 * above contact/return-address drawers still open during save.
 *
 * Per-mode title/body chrome: `addressSuggestionDrawerChrome` (MVP verify drawer).
 */
import AdaptiveDrawer, {
    type AdaptiveDrawerDrawerUi,
} from '~~/layers/base/components/adaptive/Drawer.vue'
import CraneAddressCard, {
    type CraneAddressCardAddress,
} from '~~/layers/base/components/crane/AddressCard.vue'
import CraneButton from '~~/layers/base/components/shared/CraneButton.vue'
import SharedAppIcon from '~~/layers/base/components/shared/AppIcon.vue'
import SharedCardBlock from '~~/layers/base/components/shared/CardBlock.vue'
import SharedEyebrow from '~~/layers/base/components/shared/Eyebrow.vue'
import { useAddressSuggestionOverlay } from '~~/layers/base/composables/useAddressSuggestionOverlay'
import useTopnavButtonUi from '~~/layers/base/composables/useTopnavButton'
import {
    addressSuggestionCopy,
    addressSuggestionDrawerChrome,
} from '~~/layers/base/data/crane/addressSuggestion'
import { AUTH_ALERT_CARD_BODY_UI, getAuthAlertCardConfig } from '~~/layers/base/data/auth'
import { addressPageUi } from '~~/layers/base/utils/crane/address'
import type { AddressSuggestionFields } from '~~/layers/base/utils/crane/address'
import { fiveDigitZip } from '~~/layers/base/utils/fiveDigitZip'
import { resolveAddressCardAddressee } from '~~/layers/base/utils/formatAddressLine'

const closeButtonUi = useTopnavButtonUi(computed(() => 'light' as const))
const { drawerOpen, original, suggested, addresseeContext, mode, closePicked, closeCancelled } =
    useAddressSuggestionOverlay()

/** Above nested AdaptiveDrawers (contact/return-address) while those stay open during save. */
const SUGGESTION_DRAWER_STACK_CLASS = 'z-[120]'

const drawerChrome = computed(() => addressSuggestionDrawerChrome[mode.value])

const verifyAlertConfig = computed(() =>
    getAuthAlertCardConfig({
        id: `address-verify-${mode.value}`,
        variant: drawerChrome.value.alertVariant,
        title: drawerChrome.value.alertTitle,
        body: drawerChrome.value.alertBody,
    })
)

/**
 * One ui map for this sheet. Always a bottom sheet (`always-drawer`); elevated z-index
 * so the picker appears above other open address drawers on the send flow.
 */
const ui = computed(() => ({
    shellRoot: 'relative w-full',
    closeButton: 'absolute top-0 -right-1 z-2',
    closeIcon: 'text-inherit transition-opacity duration-200 group-active:opacity-50',
    shellHead: 'flex flex-col gap-2.75 pr-15',
    eyebrow: 'm-0 mt-4',
    title: 'my-0 font-serif text-5xl font-normal text-(--primary-700) [&_em]:italic',
    subtitle: 'mt-1.25 mb-0 max-w-[38ch] text-md font-normal text-(--neutral-700)',
    body: 'bg-(--neutral-50) px-5 pb-24',
    goldRule: 'mt-0.5 mb-4.25 h-[1.5px] w-7 bg-(--gold)',
    list: 'm-0 flex list-none flex-col gap-3 p-0',
    footer: 'mt-5 mb-4 max-w-[38ch] text-md font-normal text-(--neutral-700)',
    editButton: `${addressPageUi.addButton} mt-3`,
    drawer: {
        overlay: SUGGESTION_DRAWER_STACK_CLASS,
        content: `${SUGGESTION_DRAWER_STACK_CLASS} rounded-t-[1.125rem] bg-(--neutral-50) overflow-hidden ring-0`,
        title: 'relative sticky top-0 z-10 block w-full bg-(--neutral-50) px-5 py-4 font-normal',
        body: 'p-0 overflow-y-auto bg-(--neutral-50)',
        container: 'gap-0 !p-0 bg-(--neutral-50)',
    } satisfies AdaptiveDrawerDrawerUi,
}))

function toCard(address: AddressSuggestionFields): CraneAddressCardAddress {
    const addressee = resolveAddressCardAddressee(address, addresseeContext.value)
    return {
        label: 'Address',
        prefix: addressee.prefix,
        first_name: addressee.first_name,
        last_name: addressee.last_name,
        suffix: addressee.suffix,
        street_address_1: address.street_address_1,
        street_address_2: address.street_address_2,
        city: address.city,
        state: address.state,
        zip_code: fiveDigitZip(address.zip_code) || address.zip_code,
    }
}

const suggestedCard = computed(() => toCard(suggested.value))
const originalCard = computed(() => toCard(original.value))

function dismiss() {
    closeCancelled()
}

function onEditAddress() {
    closeCancelled()
}

function onClose(result: unknown) {
    if (result && typeof result === 'object') {
        closePicked(result as AddressSuggestionFields)
        return
    }
    closeCancelled()
}
</script>
